import { createLogger } from './logger';
import { FileServiceError } from '../types/files';

const logger = createLogger({ module: 'retryUtils' });

export interface RetryOptions {
  maxAttempts: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffFactor: number;
  retryableErrors?: string[];
}

export const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  maxAttempts: 3,
  initialDelayMs: 1000, // 1 second
  maxDelayMs: 10000,    // 10 seconds
  backoffFactor: 2,
  retryableErrors: [
    'AbortError',
    'TimeoutError',
    'NetworkError',
    'ConnectionError'
  ]
};

export class RetryError extends Error {
  constructor(
    message: string,
    public readonly attempts: number,
    public readonly lastError: Error | FileServiceError
  ) {
    super(message);
    this.name = 'RetryError';
  }
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<T> {
  const finalOptions: RetryOptions = { ...DEFAULT_RETRY_OPTIONS, ...options };
  let lastError: Error | FileServiceError;
  let attempt = 1;

  while (attempt <= finalOptions.maxAttempts) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      if (attempt === finalOptions.maxAttempts || !isRetryableError(lastError)) {
        throw new RetryError(
          `Operation failed after ${attempt} attempts`,
          attempt,
          lastError
        );
      }

      const delay = Math.min(
        finalOptions.initialDelayMs * Math.pow(finalOptions.backoffFactor, attempt - 1),
        finalOptions.maxDelayMs
      );

      logger.debug(`Retry attempt ${attempt} failed, retrying in ${delay}ms`, {
        error: lastError,
        attempt,
        delay
      });

      await new Promise(resolve => setTimeout(resolve, delay));
      attempt++;
    }
  }

  throw lastError!;
}

export function isRetryableError(error: Error | FileServiceError): boolean {
  if (error instanceof FileServiceError) {
    // Add specific FileServiceError conditions here if needed
    return true;
  }
  return DEFAULT_RETRY_OPTIONS.retryableErrors?.includes(error.name) || false;
}

export async function withRetryAndFallback<T>(
  operation: () => Promise<T>,
  fallback: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<T> {
  try {
    return await withRetry(operation, options);
  } catch (error) {
    logger.warn('Primary operation failed, attempting fallback', { error });
    return await fallback();
  }
} 
